import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { BottomSheetProvider } from "./BottomSheetContext";
import "./BottomSheet.css";

interface BottomSheetProps {
  children: ReactNode;
  expandButton?: ReactNode;
  collapseButton?: ReactNode;
}

// Constants for snap points
const MIN_HEIGHT = 64;

/**
 * Bottom sheet for mobile views.
 */
const BottomSheet: FunctionComponent<BottomSheetProps> = ({
  children,
  expandButton,
  collapseButton,
}) => {
  // The component largely makes use of refs to ensure smooth animation rather than `useState`.
  const isDraggingRef = useRef(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const lastTouchY = useRef(0);
  // Instead of currentHeight, track currentTranslateY
  const currentTranslateY = useRef(0);
  const animationFrameRef = useRef<number>(null);
  const [currentSnapPoint, setCurrentSnapPoint] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const animateToTranslateYRef = useRef<(y: number) => void>(null);

  // Get the max height (expanded state)
  const getMaxHeight = () => {
    const mainContent = document.getElementById("main-content");
    if (mainContent) {
      // Add 16px to account for bottom sheet handle, so it is covered by header.
      return mainContent.offsetHeight + 28;
    }
    return window.innerHeight;
  };

  // Animate to target translateY with easing
  const animateToTranslateY = useCallback((targetY: number) => {
    if (!drawerRef.current) {
      return;
    }

    const startY = currentTranslateY.current;
    const duration = 300; // animation duration in ms
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const newY = startY + (targetY - startY) * easeOut;
      currentTranslateY.current = newY;

      if (drawerRef.current) {
        drawerRef.current.style.transform = `translateY(${newY}px)`;
      }

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    // Cancel any ongoing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  // Assign animateToTranslateY to ref so it can be called from context
  animateToTranslateYRef.current = animateToTranslateY;

  // Determine which snap point to use based on current translateY and direction
  const snapToPoint = useCallback(
    (force?: boolean) => {
      const maxHeight = getMaxHeight();
      const range = maxHeight - MIN_HEIGHT;

      // Different thresholds for different directions
      const UP_THRESHOLD = maxHeight - MIN_HEIGHT - range * 0.1;
      const DOWN_THRESHOLD = range * 0.1;
      let isToggled = false;

      if (currentSnapPoint === 0) {
        // When moving UP, if we've passed the UP_THRESHOLD, snap to expanded
        if (currentTranslateY.current < UP_THRESHOLD || force) {
          animateToTranslateY(0);
          isToggled = true;
        } else {
          animateToTranslateY(maxHeight - MIN_HEIGHT);
        }
      } else if (currentSnapPoint === 1) {
        // When moving DOWN, if we've passed the DOWN_THRESHOLD, snap to collapsed
        if (currentTranslateY.current > DOWN_THRESHOLD || force) {
          animateToTranslateY(maxHeight - MIN_HEIGHT);
          isToggled = true;
        } else {
          animateToTranslateY(0);
        }
      }

      if (isToggled) {
        setCurrentSnapPoint((prev) => (prev === 0 ? 1 : 0));
        drawerRef.current?.classList.toggle("expanded", currentSnapPoint === 1);
      }
    },
    [currentSnapPoint, animateToTranslateY],
  );

  const handleExpandCollapse = useCallback(() => {
    snapToPoint(true);
  }, [snapToPoint]);

  const handleClose = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      animateToTranslateY(getMaxHeight());
    }
  }, [isOpen, animateToTranslateY]);

  const handleOpen = useCallback(() => {
    if (!isOpen) {
      // Wait for other animations before opening.
      setTimeout(() => {
        setIsOpen(true);
        animateToTranslateY(
          currentSnapPoint === 1 ? 0 : getMaxHeight() - MIN_HEIGHT,
        );
      }, 400);
    }
  }, [isOpen, animateToTranslateY, currentSnapPoint]);

  const onTouchMove = (e: TouchEvent) => {
    if (!isDraggingRef.current || !drawerRef.current) {
      return;
    }

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY.current;

    // Update last Y for direction calculation
    lastTouchY.current = startY.current;
    startY.current = currentY;

    // Constrain translateY between 0 (expanded) and maxHeight - MIN_HEIGHT (collapsed)
    const maxHeight = getMaxHeight();
    const minY = 0;
    const maxY = maxHeight - MIN_HEIGHT;
    const newY = Math.max(
      minY,
      Math.min(maxY, currentTranslateY.current + deltaY),
    );

    currentTranslateY.current = newY;
    drawerRef.current.style.transform = `translateY(${newY}px)`;
  };

  const onTouchEnd = () => {
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;

    snapToPoint();
  };

  useEffect(() => {
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePointerDown = (e: React.TouchEvent) => {
    isDraggingRef.current = true;
    startY.current = e.touches[0].clientY;
    lastTouchY.current = e.touches[0].clientY;

    // Cancel any ongoing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  useEffect(() => {
    // Initialize drawer at collapsed position
    if (drawerRef.current) {
      const maxHeight = getMaxHeight();
      const initialY = maxHeight - MIN_HEIGHT;
      drawerRef.current.style.transform = `translateY(${initialY}px)`;
      currentTranslateY.current = initialY;
    }
  }, []);

  return (
    <BottomSheetProvider
      collapse={handleExpandCollapse}
      expand={handleExpandCollapse}
      close={handleClose}
      open={handleOpen}>
      <div
        className={`bottom-sheet-component ${
          currentSnapPoint === 1 ? "expanded" : ""
        }`}
        ref={drawerRef}
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          height: "100vh",
          willChange: "transform",
        }}>
        <div className="handle" onTouchStart={handlePointerDown}>
          <div className="handle-line" />
          {expandButton && currentSnapPoint === 0 && expandButton}
        </div>

        {children}
        {collapseButton && isOpen && currentSnapPoint === 1 && collapseButton}
      </div>
    </BottomSheetProvider>
  );
};

export { BottomSheet };
